import React, { useState, useMemo, useEffect, memo } from "react"
import PropTypes from "prop-types"
import { formatCurrency, formatNumber, formatPercent, formatFecha, IMPUESTOS_LABELS, TIPO_COMPROBANTE, origenConfig } from "../helpers"
import CopiarNit from "../CopiarNit"

const PAGE_SIZE = 50
const PAGE_SIZE_OPCIONES = [25, 50, 100, 250]

const cellSticky = {
  padding: "10px 12px", fontSize: 11, fontWeight: 600,
  color: "#475569", textTransform: "uppercase", letterSpacing: 0.4,
  borderBottom: "1px solid #e2e8f0", background: "#f1f5f9",
  position: "sticky", top: 0, zIndex: 1,
}

const HeaderSort = ({ campo, orden, onClick, align = "left", children }) => {
  const activo = orden.campo === campo
  return (
    <th
      onClick={() => onClick(campo)}
      style={{ ...cellSticky, textAlign: align, cursor: "pointer", userSelect: "none", color: activo ? "#0f172a" : "#475569" }}
    >
      {children}
      <i className={`mdi mdi-${activo ? (orden.asc ? "chevron-up" : "chevron-down") : "unfold-more-horizontal"} ms-1`}
        style={{ opacity: activo ? 1 : 0.4, fontSize: 14 }} />
    </th>
  )
}

HeaderSort.propTypes = { campo: PropTypes.string, orden: PropTypes.object, onClick: PropTypes.func, align: PropTypes.string, children: PropTypes.node }

const ORDEN_CAMPOS = {
  nombre: t => (t.nombre_canonico || t.nombrereceptor || "").toLowerCase(),
  total_facturas: t => Number(t.total_facturas) || 0,
  total_ventas: t => Number(t.total_ventas) || 0,
  total_impuestos: t => Number(t.total_impuestos) || 0,
  promedio_venta: t => Number(t.promedio_venta) || 0,
  ultima_factura: t => t.ultima_factura || "",
}

const VarianteRow = ({ v }) => (
  <tr style={{ borderBottom: "1px solid #f1f5f9" }}>
    <td style={{ padding: "6px 10px", fontSize: 12, color: "#0f172a" }}>
      <i className="mdi mdi-account-arrow-right text-muted me-1" />
      {v.nombre}
    </td>
    <td style={{ padding: "6px 10px", textAlign: "right", fontSize: 12, color: "#475569" }}>{formatNumber(v.facturas)}</td>
    <td style={{ padding: "6px 10px", textAlign: "right", fontSize: 12, color: "#94a3b8" }}>{formatFecha(v.ultimo_uso)}</td>
  </tr>
)

VarianteRow.propTypes = { v: PropTypes.object }

const DesgloseRow = ({ d }) => (
  <tr style={{ borderBottom: "1px solid #f1f5f9", background: "#fff" }}>
    <td style={{ padding: "6px 10px", fontSize: 12, color: "#475569" }}>
      <span style={{ background: "#e0e7ff", color: "#3730a3", padding: "1px 6px", borderRadius: 4, fontSize: 10, fontWeight: 700, marginRight: 6 }}>
        {d.tipo_comprobante}
      </span>
      {TIPO_COMPROBANTE[d.tipo_comprobante] || d.descripcion_tipo || "—"}
    </td>
    <td style={{ padding: "6px 10px", textAlign: "right", fontSize: 12, color: "#475569" }}>{formatNumber(d.total_documentos)}</td>
    <td style={{ padding: "6px 10px", textAlign: "right", fontSize: 12, color: "#10b981", fontWeight: 600 }}>{formatCurrency(d.total_subtotal)}</td>
    <td style={{ padding: "6px 10px", textAlign: "right", fontSize: 12, color: "#f59e0b", fontWeight: 600 }}>{formatCurrency(d.total_impuestos)}</td>
    <td style={{ padding: "6px 10px", textAlign: "right", fontSize: 12, color: "#0f172a", fontWeight: 700 }}>{formatCurrency(d.total_ventas)}</td>
    <td style={{ padding: "6px 10px", textAlign: "right", fontSize: 12, color: "#475569" }}>{d.participacion ?? "—"}</td>
  </tr>
)

DesgloseRow.propTypes = { d: PropTypes.object }

const FilaTerceroBase = ({ tercero, expandida, onToggle, onEditarCanonico }) => {
  const nombre = tercero.nombre_canonico || tercero.nombrereceptor || "—"
  const origen = origenConfig(tercero.nombre_canonico_origen)
  const variantes = tercero.nombre_variantes || []

  return (
    <>
      <tr
        style={{
          background: expandida
            ? "#eff6ff"
            : tercero.tiene_variantes
              ? "#fffbeb"
              : "#fff",
          borderBottom: "1px solid #f1f5f9",
          cursor: "pointer",
        }}
        onClick={onToggle}
      >
        <td style={{ padding: "12px 12px", textAlign: "center", color: "#64748b" }}>
          <i className={`mdi mdi-chevron-${expandida ? "down" : "right"}`} style={{ fontSize: 18 }} />
        </td>
        <td style={{ padding: "12px 12px" }} onClick={e => e.stopPropagation()}>
          <CopiarNit nit={tercero.nitreceptor} />
        </td>
        <td style={{ padding: "12px 12px" }}>
          <div style={{ color: "#0f172a", fontWeight: 600, display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
            <span>{nombre}</span>
            {tercero.nombre_canonico_origen && (
              <span
                style={{
                  background: origen.bg, color: origen.color,
                  padding: "1px 7px", borderRadius: 4,
                  fontSize: 10, fontWeight: 700, textTransform: "uppercase",
                }}
                title={origen.desc}
              >
                <i className={`mdi mdi-${origen.icon} me-1`} />
                {origen.label}
              </span>
            )}
            {tercero.tiene_variantes && (
              <span
                style={{
                  background: "#fef3c7", color: "#92400e",
                  padding: "1px 7px", borderRadius: 4,
                  fontSize: 10, fontWeight: 700, textTransform: "uppercase",
                }}
                title={`${variantes.length} variantes detectadas: ${variantes.slice(0, 3).map(v => v.nombre).join(", ")}${variantes.length > 3 ? "…" : ""}`}
              >
                <i className="mdi mdi-account-alert-outline me-1" />
                {variantes.length} variantes
              </span>
            )}
            <button
              type="button"
              onClick={e => { e.stopPropagation(); onEditarCanonico(tercero) }}
              title="Editar nombre canónico"
              style={{
                background: "none", border: "none", padding: "1px 4px",
                color: "#3b82f6", cursor: "pointer",
                display: "inline-flex", alignItems: "center",
              }}
            >
              <i className="mdi mdi-pencil" style={{ fontSize: 14 }} />
            </button>
          </div>
          {tercero.nombrereceptor && tercero.nombre_canonico && tercero.nombrereceptor !== tercero.nombre_canonico && (
            <div style={{ fontSize: 11, color: "#94a3b8", marginTop: 2 }}>
              Legacy: {tercero.nombrereceptor}
            </div>
          )}
        </td>
        <td style={{ padding: "12px 12px", textAlign: "right" }}>
          <span style={{ background: "#e0e7ff", color: "#3730a3", padding: "2px 10px", borderRadius: 999, fontSize: 12, fontWeight: 600 }}>
            {formatNumber(tercero.total_facturas)}
          </span>
        </td>
        <td style={{ padding: "12px 12px", textAlign: "right", color: "#10b981", fontWeight: 600 }}>
          {formatCurrency(tercero.total_subtotal)}
        </td>
        <td style={{ padding: "12px 12px", textAlign: "right", color: "#f59e0b", fontWeight: 600 }}>
          {formatCurrency(tercero.total_impuestos)}
        </td>
        <td style={{ padding: "12px 12px", textAlign: "right", color: "#0f172a", fontWeight: 700 }}>
          {formatCurrency(tercero.total_ventas)}
        </td>
        <td style={{ padding: "12px 12px", textAlign: "right", color: "#475569" }}>
          {formatCurrency(tercero.promedio_venta)}
        </td>
        <td style={{ padding: "12px 12px", textAlign: "right", color: "#94a3b8", fontSize: 12 }}>
          {formatFecha(tercero.ultima_factura)}
        </td>
      </tr>

      {expandida && (
        <tr style={{ background: "#fafafa" }}>
          <td />
          <td colSpan={8} style={{ padding: "16px 20px" }}>
            <div className="row g-3">
              {/* Desglose por tipo de comprobante */}
              {(tercero.desglose_por_tipo || []).length > 0 && (
                <div className="col-lg-7">
                  <h6 style={{ fontSize: 11, color: "#64748b", textTransform: "uppercase", fontWeight: 600, letterSpacing: 0.4, marginBottom: 8 }}>
                    <i className="mdi mdi-file-multiple-outline me-1" />
                    Desglose por tipo de comprobante
                  </h6>
                  <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12, background: "#fff", borderRadius: 6, overflow: "hidden", border: "1px solid #e2e8f0" }}>
                    <thead style={{ background: "#f8fafc" }}>
                      <tr>
                        <th style={{ padding: "6px 10px", textAlign: "left", fontSize: 10, color: "#64748b", textTransform: "uppercase", fontWeight: 600 }}>Tipo</th>
                        <th style={{ padding: "6px 10px", textAlign: "right", fontSize: 10, color: "#64748b", textTransform: "uppercase", fontWeight: 600 }}>Docs</th>
                        <th style={{ padding: "6px 10px", textAlign: "right", fontSize: 10, color: "#64748b", textTransform: "uppercase", fontWeight: 600 }}>Subtotal</th>
                        <th style={{ padding: "6px 10px", textAlign: "right", fontSize: 10, color: "#64748b", textTransform: "uppercase", fontWeight: 600 }}>Impuestos</th>
                        <th style={{ padding: "6px 10px", textAlign: "right", fontSize: 10, color: "#64748b", textTransform: "uppercase", fontWeight: 600 }}>Total</th>
                        <th style={{ padding: "6px 10px", textAlign: "right", fontSize: 10, color: "#64748b", textTransform: "uppercase", fontWeight: 600 }}>%</th>
                      </tr>
                    </thead>
                    <tbody>
                      {tercero.desglose_por_tipo.map((d, i) => <DesgloseRow key={i} d={d} />)}
                    </tbody>
                  </table>
                </div>
              )}

              {/* Detalle impuestos */}
              <div className="col-lg-5">
                <h6 style={{ fontSize: 11, color: "#64748b", textTransform: "uppercase", fontWeight: 600, letterSpacing: 0.4, marginBottom: 8 }}>
                  <i className="mdi mdi-bank me-1" />
                  Detalle de impuestos
                </h6>
                <div style={{
                  background: "#fff", border: "1px solid #e2e8f0", borderRadius: 6,
                  padding: 8,
                  display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(110px, 1fr))", gap: 6,
                }}>
                  {IMPUESTOS_LABELS
                    .filter(({ key }) => Number(tercero.detalle_impuestos?.[key]) > 0)
                    .map(({ key, label }) => (
                      <div key={key} style={{ background: "#f8fafc", padding: "6px 8px", borderRadius: 4 }}>
                        <div style={{ fontSize: 10, color: "#64748b", textTransform: "uppercase", fontWeight: 600 }}>{label}</div>
                        <div style={{ fontSize: 12, color: "#0f172a", fontWeight: 700 }}>
                          {formatCurrency(tercero.detalle_impuestos[key])}
                        </div>
                      </div>
                    ))
                  }
                </div>
              </div>

              {/* Variantes de nombre */}
              {variantes.length > 0 && (
                <div className="col-12">
                  <h6 style={{ fontSize: 11, color: "#64748b", textTransform: "uppercase", fontWeight: 600, letterSpacing: 0.4, marginBottom: 8 }}>
                    <i className="mdi mdi-account-multiple-outline me-1" />
                    Variantes de nombre detectadas
                  </h6>
                  <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12, background: "#fff", borderRadius: 6, overflow: "hidden", border: "1px solid #e2e8f0" }}>
                    <thead style={{ background: "#f8fafc" }}>
                      <tr>
                        <th style={{ padding: "6px 10px", textAlign: "left", fontSize: 10, color: "#64748b", textTransform: "uppercase", fontWeight: 600 }}>Nombre</th>
                        <th style={{ padding: "6px 10px", textAlign: "right", fontSize: 10, color: "#64748b", textTransform: "uppercase", fontWeight: 600 }}>Facturas</th>
                        <th style={{ padding: "6px 10px", textAlign: "right", fontSize: 10, color: "#64748b", textTransform: "uppercase", fontWeight: 600 }}>Último uso</th>
                      </tr>
                    </thead>
                    <tbody>
                      {variantes.map((v, i) => <VarianteRow key={i} v={v} />)}
                    </tbody>
                  </table>
                </div>
              )}

              {/* Periodo */}
              <div className="col-12">
                <div style={{ fontSize: 11, color: "#94a3b8", display: "flex", gap: 16, flexWrap: "wrap" }}>
                  <span><strong>Sucursales atendidas:</strong> {formatNumber(tercero.sucursales_atendidas)}</span>
                  {tercero.total_propina > 0 && <span><strong>Propinas:</strong> {formatCurrency(tercero.total_propina)}</span>}
                  <span><strong>Primera factura:</strong> {formatFecha(tercero.primera_factura)}</span>
                  <span><strong>Última factura:</strong> {formatFecha(tercero.ultima_factura)}</span>
                </div>
              </div>
            </div>
          </td>
        </tr>
      )}
    </>
  )
}

FilaTerceroBase.propTypes = { tercero: PropTypes.object, expandida: PropTypes.bool, onToggle: PropTypes.func, onEditarCanonico: PropTypes.func }

// Memo: solo re-renderea si cambian estas props (clave para 8k+ filas)
const FilaTercero = memo(FilaTerceroBase, (prev, next) =>
  prev.tercero === next.tercero &&
  prev.expandida === next.expandida &&
  prev.onToggle === next.onToggle &&
  prev.onEditarCanonico === next.onEditarCanonico,
)

const TercerosTab = ({ data, onEditarCanonico }) => {
  const terceros = data?.terceros || []
  const [busquedaInput, setBusquedaInput] = useState("")
  const [busqueda, setBusqueda] = useState("")
  const [orden, setOrden] = useState({ campo: "total_ventas", asc: false })
  const [expandidas, setExpandidas] = useState(new Set())
  const [soloVariantes, setSoloVariantes] = useState(false)
  const [pagina, setPagina] = useState(0)
  const [pageSize, setPageSize] = useState(PAGE_SIZE)

  // Debounce de búsqueda: evita re-filtrar 8k items con cada keystroke
  useEffect(() => {
    const t = setTimeout(() => setBusqueda(busquedaInput), 300)
    return () => clearTimeout(t)
  }, [busquedaInput])

  // Reset de paginación cuando cambian filtros u orden
  useEffect(() => { setPagina(0) }, [busqueda, soloVariantes, orden, pageSize])

  const filtrados = useMemo(() => {
    const q = busqueda.toLowerCase()
    let lista = terceros
    if (soloVariantes) lista = lista.filter(t => t.tiene_variantes)
    if (q) {
      lista = lista.filter(t => {
        if ((t.nitreceptor || "").toLowerCase().includes(q)) return true
        if ((t.nombre_canonico || "").toLowerCase().includes(q)) return true
        if ((t.nombrereceptor || "").toLowerCase().includes(q)) return true
        return (t.nombre_variantes || []).some(v => (v.nombre || "").toLowerCase().includes(q))
      })
    }
    if (!orden.campo) return lista
    const getter = ORDEN_CAMPOS[orden.campo]
    if (!getter) return lista
    const dir = orden.asc ? 1 : -1
    return [...lista].sort((a, b) => {
      const av = getter(a)
      const bv = getter(b)
      if (typeof av === "string") return av.localeCompare(bv) * dir
      return ((av || 0) - (bv || 0)) * dir
    })
  }, [terceros, busqueda, orden, soloVariantes])

  const totalPaginas = Math.max(1, Math.ceil(filtrados.length / pageSize))
  const paginaActual = Math.min(pagina, totalPaginas - 1)
  const desde = paginaActual * pageSize
  const hasta = Math.min(desde + pageSize, filtrados.length)
  const paginados = useMemo(() => filtrados.slice(desde, hasta), [filtrados, desde, hasta])

  const toggleOrden = campo =>
    setOrden(prev => prev.campo === campo ? { campo, asc: !prev.asc } : { campo, asc: false })

  const toggle = nit => {
    setExpandidas(prev => {
      const next = new Set(prev)
      if (next.has(nit)) next.delete(nit)
      else next.add(nit)
      return next
    })
  }

  const totalFiltrado = filtrados.reduce((s, t) => s + (Number(t.total_ventas) || 0), 0)

  return (
    <div style={{ background: "#fff", borderRadius: 14, padding: 20, border: "1px solid #e2e8f0" }}>
      <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-3">
        <h6 style={{ margin: 0, color: "#0f172a", fontWeight: 600 }}>
          <i className="mdi mdi-account-group me-2 text-primary" />
          Terceros ({formatNumber(filtrados.length)} de {formatNumber(terceros.length)})
          {totalFiltrado > 0 && (
            <span style={{ fontWeight: 400, color: "#64748b", marginLeft: 12, fontSize: 13 }}>
              · Total: <strong style={{ color: "#0f172a" }}>{formatCurrency(totalFiltrado)}</strong>
            </span>
          )}
        </h6>
        <div className="d-flex align-items-center gap-2 flex-wrap">
          <div style={{ position: "relative", maxWidth: 280 }}>
            <i className="mdi mdi-magnify" style={{ position: "absolute", left: 10, top: "50%", transform: "translateY(-50%)", color: "#94a3b8" }} />
            <input
              type="text"
              className="form-control form-control-sm"
              placeholder="Buscar NIT, nombre o variante…"
              value={busquedaInput}
              onChange={e => setBusquedaInput(e.target.value)}
              style={{ paddingLeft: 32, minWidth: 240 }}
            />
          </div>
          <div className="form-check form-switch m-0">
            <input
              type="checkbox"
              className="form-check-input"
              role="switch"
              id="soloVariantes"
              checked={soloVariantes}
              onChange={e => setSoloVariantes(e.target.checked)}
            />
            <label className="form-check-label small" htmlFor="soloVariantes" style={{ color: "#475569" }}>
              Solo con variantes
            </label>
          </div>
        </div>
      </div>

      <div style={{ border: "1px solid #e2e8f0", borderRadius: 8, overflow: "hidden" }}>
        <div style={{ maxHeight: 640, overflowY: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
            <thead>
              <tr>
                <th style={{ ...cellSticky, width: 40 }} />
                <th style={cellSticky}>NIT</th>
                <HeaderSort campo="nombre" orden={orden} onClick={toggleOrden}>Nombre canónico</HeaderSort>
                <HeaderSort campo="total_facturas" orden={orden} onClick={toggleOrden} align="right">Facturas</HeaderSort>
                <HeaderSort campo="total_subtotal" orden={orden} onClick={toggleOrden} align="right">Subtotal</HeaderSort>
                <HeaderSort campo="total_impuestos" orden={orden} onClick={toggleOrden} align="right">Impuestos</HeaderSort>
                <HeaderSort campo="total_ventas" orden={orden} onClick={toggleOrden} align="right">Total</HeaderSort>
                <HeaderSort campo="promedio_venta" orden={orden} onClick={toggleOrden} align="right">Ticket prom.</HeaderSort>
                <HeaderSort campo="ultima_factura" orden={orden} onClick={toggleOrden} align="right">Última</HeaderSort>
              </tr>
            </thead>
            <tbody>
              {filtrados.length === 0 && (
                <tr><td colSpan={9} style={{ padding: 40, textAlign: "center", color: "#64748b" }}>
                  Sin terceros para los filtros aplicados.
                </td></tr>
              )}
              {paginados.map(t => (
                <FilaTercero
                  key={t.nitreceptor}
                  tercero={t}
                  expandida={expandidas.has(t.nitreceptor)}
                  onToggle={() => toggle(t.nitreceptor)}
                  onEditarCanonico={onEditarCanonico}
                />
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Paginador */}
      {filtrados.length > 0 && (
        <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mt-3">
          <div style={{ fontSize: 12, color: "#64748b" }}>
            Mostrando <strong style={{ color: "#0f172a" }}>{desde + 1}</strong>–
            <strong style={{ color: "#0f172a" }}>{hasta}</strong> de{" "}
            <strong style={{ color: "#0f172a" }}>{formatNumber(filtrados.length)}</strong>
          </div>
          <div className="d-flex align-items-center gap-2">
            <select
              className="form-select form-select-sm"
              style={{ width: "auto", fontSize: 12 }}
              value={pageSize}
              onChange={e => setPageSize(Number(e.target.value))}
              title="Filas por página"
            >
              {PAGE_SIZE_OPCIONES.map(n => <option key={n} value={n}>{n} / página</option>)}
            </select>
            <button
              type="button"
              className="btn btn-sm btn-outline-secondary"
              onClick={() => setPagina(0)}
              disabled={paginaActual === 0}
              title="Primera"
            >
              <i className="mdi mdi-page-first" />
            </button>
            <button
              type="button"
              className="btn btn-sm btn-outline-secondary"
              onClick={() => setPagina(p => Math.max(0, p - 1))}
              disabled={paginaActual === 0}
            >
              <i className="mdi mdi-chevron-left" /> Anterior
            </button>
            <span style={{ fontSize: 12, color: "#475569", fontWeight: 600, minWidth: 80, textAlign: "center" }}>
              {paginaActual + 1} / {totalPaginas}
            </span>
            <button
              type="button"
              className="btn btn-sm btn-outline-secondary"
              onClick={() => setPagina(p => Math.min(totalPaginas - 1, p + 1))}
              disabled={paginaActual >= totalPaginas - 1}
            >
              Siguiente <i className="mdi mdi-chevron-right" />
            </button>
            <button
              type="button"
              className="btn btn-sm btn-outline-secondary"
              onClick={() => setPagina(totalPaginas - 1)}
              disabled={paginaActual >= totalPaginas - 1}
              title="Última"
            >
              <i className="mdi mdi-page-last" />
            </button>
          </div>
        </div>
      )}
    </div>
  )
}

TercerosTab.propTypes = { data: PropTypes.object, onEditarCanonico: PropTypes.func.isRequired }

export default TercerosTab
